import { FrameworkService } from '@/services/framework.service';
import { Helpers } from '@/common/helpers';

function getAuthoredSetttings(uuid) {
	if (uuid && document.getElementById(uuid)) {
		const jsonData = document.getElementById(uuid).innerHTML;
		if (jsonData.trim().indexOf('{') > -1 || jsonData.trim().indexOf('[') > -1) {
			return JSON.parse(jsonData);
		}
	}
	return {};
}

function hasSessionStorage() {
	var check = 'sample';
	try {
		sessionStorage.setItem(check, check);
		sessionStorage.removeItem(check);
		return true;
	} catch {
		return false;
	}
}

function stringArrayToIntArray(arr) {
	return arr.map((val) => parseInt(val, 10));
}

let __stateServiceInitializedInMixin = false;

const storageKeyPrefix = 'ltt-cmp-schreg-reservation-';

export const StateService = {
	authoredSettings: {},
	queryData: new URLSearchParams(),
	hasSessionStorage: false,

	initializeMixin(el) {
		if (!__stateServiceInitializedInMixin) {
			__stateServiceInitializedInMixin = true;
			this.setQueryStringData();
			this.setAuthoredSettings(el);
			this.setSessionStorageFlag();
		}
	},

	setQueryStringData() {
		this.queryData = new URLSearchParams(window.location.search);
	},
	setAuthoredSettings(vueElement) {
		if (vueElement.parentElement !== null) {
			const uuid = vueElement.parentElement.getAttribute('data-uuid');
			this.authoredSettings = getAuthoredSetttings(uuid);
		}
	},
	setSessionStorageFlag() {
		this.hasSessionStorage = hasSessionStorage();
	},

	getQuery(key) {
		const queryParams = this.queryData.getAll(key);

		if (queryParams.length === 0) return null;
		return queryParams.length > 1 ? queryParams : queryParams[0];
	},

	setQueryStringKey(key, valueOrArray) {
		if (Array.isArray(valueOrArray)) {
			this.queryData.delete(key);
			valueOrArray.forEach((val) => this.queryData.append(key, val));
		} else {
			this.queryData.set(key, valueOrArray);
		}
		this.replaceQueryStringFromState();
	},

	removeQueryStringKey(key) {
		this.queryData.delete(key);
		this.replaceQueryStringFromState();
	},

	replaceQueryStringFromState() {
		var uri = this.queryData.toString();
		uri = location.pathname + (uri ? '?' + uri : '');
		window.history.replaceState({ path: uri.substr(1) }, '', uri);
	},

	// FrameworkService data
	getLocalProfileStore() {
		if (!Helpers.isDev()) return null;
		return this.getDevSettings().profileStore;
	},
	getProfileToken() {
		if (!Helpers.isDev()) return FrameworkService.data('profile.token');

		const dev = this.getDevSettings();
		return dev.sendProfile && dev.profile ? dev.profile : null;
	},
	getSSOToken() {
		if (!Helpers.isDev()) return FrameworkService.data('profile.sso');

		const dev = this.getDevSettings();
		return dev.sendSso && dev.sso ? dev.sso : null;
	},
	getAuthToken() {
		return FrameworkService.data('profile.authToken');
	},
	getJwe() {
		if (!Helpers.isDev()) return FrameworkService.getJwe();

		const dev = this.getDevSettings();
		return dev.sendJwe && dev.jwe ? dev.jwe : null;
	},
	isRemembered() {
		return (this.getProfileToken() || '') !== '';
	},
	isAuthenticated() {
		return (this.getSSOToken() || this.getAuthToken() || this.getJwe() || '') !== '';
	},
	getLocationName() {
		// Note: club.name is populated from prospect.clubName as necessary in real AEM environments, so it's safe to just use club.name
		if (!Helpers.isDev()) return FrameworkService.data('club.name');
		return this.getDevSettings().club || 'Chanhassen';
	},
	getClubId() {
		if (!Helpers.isDev()) return FrameworkService.data('club.id');
		return this.getDevSettings().clubId || 151;
	},
	getClassSearchFormat() {
		if (!Helpers.isDev()) return 'format:Class';
		return this.getDevSettings().searchFormatEvent ? 'format:Event' : 'format:Class';
	},

	getApimRoot() {
		return FrameworkService.APIM_ROOT;
	},
	getApimKey() {
		return FrameworkService.APIM_KEY;
	},
	getLoggedInMemberId() {
		return parseInt(FrameworkService.data('profile.memberId'), 10);
	},
	getAllMemberIdArray() {
		const members = FrameworkService.data('profile.members');
		return members ? members.map((r) => r.memberId) : [];
	},
	getMemberIdArray() {
		const _memberId = this.getQuery('memberId') || FrameworkService.data('profile.memberId');
		return stringArrayToIntArray(_memberId.toString().replace(/\s/g, '').split(','));
	},
	getMemberIds() {
		const _memberId = this.getQuery('memberId');
		if (_memberId) return stringArrayToIntArray(_memberId.toString().replace(/\s/g, '').split(','));
		return [];
	},
	getCartSummaryUrl(cartId) {
		var url = FrameworkService.cartUrl('review');
		if (url) {
			url += '?cartId=' + cartId;
		}
		return url;
	},
	getExerpCartSummaryUrl() {
		return FrameworkService.accountUrl('cartPayment');
	},

	// sessionStorage data
	sessionKeyExists(key) {
		if (!this.hasSessionStorage) return false;
		const data = JSON.parse(sessionStorage.getItem(storageKeyPrefix + key)) || null;
		return data ? true : false;
	},
	getSessionJson(key) {
		return JSON.parse(sessionStorage.getItem(storageKeyPrefix + key)) || null;
	},
	removeSessionKey(key) {
		sessionStorage.removeItem(storageKeyPrefix + key);
	},
	setSessionKey(key, value) {
		sessionStorage.setItem(storageKeyPrefix + key, value);
	},
	setSessionKeyWithoutPrefix(key, value) {
		sessionStorage.setItem(key, value);
	},

	// localStorage data
	localKeyExists(key) {
		if (!this.hasLocalStorage) return false;
		const data = JSON.parse(localStorage.getItem(storageKeyPrefix + key)) || null;
		return data ? true : false;
	},
	getLocalJson(key) {
		return JSON.parse(localStorage.getItem(storageKeyPrefix + key)) || null;
	},
	removeLocalKey(key) {
		localStorage.removeItem(storageKeyPrefix + key);
	},
	setLocalKey(key, value) {
		localStorage.setItem(storageKeyPrefix + key, value);
	},

	// development settings
	getDevSettings() {
		if (Helpers.isUiTest()) {
			return {
				sso: 'SSO',
				profile: 'Profile',
				jwe: 'JWE',
				mock: false,
				sendSso: true,
				sendProfile: true,
				club: 'Chanhassen',
				clubId: 151,
				searchFormatEvent: false,
				panelVisible: false,
				profileStore: null,
				experience: 'member',
				sourceIsUi: true
			};
		}
		return this.getLocalJson('dev-settings-local') || {
			sso: '',
			profile: '',
			jwe: '',
			mock: true,
			sendSso: true,
			sendProfile: true,
			club: 'Chanhassen',
			clubId: 151,
			searchFormatEvent: false,
			panelVisible: true,
			profileStore: null,
			experience: 'member',
			sourceIsUi: false
		};
	},
	setDevSettings(settings) {
		if (Helpers.isDev()) {
			this.setLocalKey('dev-settings-local', JSON.stringify(settings));
		} else {
			this.setLocalKey('dev-settings-ui', JSON.stringify(settings));
		}
	},
	isMockData() {
		if (!Helpers.isDev()) return false;
		return (this.getDevSettings() || { mock: true }).mock;
	}
};
